import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from 'Text';
import { ClickableText } from 'ClickableText';
import summaryIcon from 'vectors/summary.svg';
import { type ResourceDownload, type ResourceLink } from 'Lessons';
import { type StreamSummaryLink } from '../StreamDashboard.types';
import { SidebarBox } from './SidebarBox';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    !list-none and !ps-2.5 beat `.stream-dashboard ul`. Additional Resources items are 10px apart by request.
*/

type Props = {
    summaries: StreamSummaryLink[];
    resourceDownloads: ResourceDownload[];
    resourceLinks: ResourceLink[];

    // True while offline, or when the student's curriculum status is failed.
    disabled: boolean;
    onSummaryClick: (summaryId: string) => void;
    onLinkClick: (url: string) => void;
    className?: string;
};

const SectionHeading: FC<{ children: string }> = ({ children }) => (
    <Text as="h3" color="black" weight="normal" className="my-3 text-lg">
        {children}
    </Text>
);

export const ResourceLinksBox: FC<Props> = ({
    summaries,
    resourceDownloads,
    resourceLinks,
    disabled,
    onSummaryClick,
    onLinkClick,
    className,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    if (!summaries.length && !resourceDownloads.length && !resourceLinks.length) return null;

    return (
        <SidebarBox caption={t('resources')} className={className}>
            {summaries.length > 0 && (
                <>
                    <SectionHeading>{t('course_summaries')}</SectionHeading>
                    <ul className="m-0 !list-none p-0 !ps-2.5">
                        {summaries.map(summary => (
                            <li key={summary.id} className="mb-3.5 last:mb-0">
                                <ClickableText
                                    onPress={() => onSummaryClick(summary.id)}
                                    disabled={disabled}
                                    color="primary"
                                    underline="none"
                                    weight="semibold"
                                    allowWrap
                                    className="flex items-start gap-2.5 text-start text-sm"
                                >
                                    {/* 38px, which is what .summary-icon measured in this box. The lesson
                                        row uses the same file at 25px, through .icon-container.small. */}
                                    <img
                                        src={summaryIcon}
                                        alt=""
                                        width="38"
                                        height="38"
                                        className="h-9.5 w-9.5 shrink-0"
                                        data-no-retina
                                    />
                                    <span>
                                        {!disabled && (
                                            <Text as="div" className="text-sm text-slate-grey no-underline">
                                                {t('tap_to_download')}
                                            </Text>
                                        )}
                                        {summary.title}
                                    </span>
                                </ClickableText>
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {(resourceDownloads.length > 0 || resourceLinks.length > 0) && (
                <>
                    <SectionHeading>{t('additional_resources')}</SectionHeading>
                    <ul className="m-0 !list-none p-0 !ps-2.5">
                        {resourceDownloads.map(download => (
                            <li key={download.id} className="mb-2.5 last:mb-0">
                                <ClickableText
                                    onPress={() => onLinkClick(download.url)}
                                    disabled={disabled}
                                    color="primary"
                                    underline="none"
                                    weight="semibold"
                                    allowWrap
                                    className="text-start text-sm"
                                >
                                    {download.title}
                                    {download.type && (
                                        <span className="ms-1 font-normal uppercase text-slate-grey no-underline">
                                            ({download.type})
                                        </span>
                                    )}
                                </ClickableText>
                            </li>
                        ))}
                        {resourceLinks.map(link => (
                            <li key={link.url} className="mb-2.5 last:mb-0">
                                <ClickableText
                                    onPress={() => onLinkClick(link.url)}
                                    disabled={disabled}
                                    color="primary"
                                    underline="none"
                                    weight="semibold"
                                    allowWrap
                                    className="text-start text-sm"
                                >
                                    {link.title}
                                </ClickableText>
                                <Text as="div" className="truncate text-3xs font-normal leading-3 text-slate-grey">
                                    {link.url}
                                </Text>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </SidebarBox>
    );
};
